import { OverlayViewF } from '@react-google-maps/api'
import type { MouseEventHandler } from 'react'
import { memo, useCallback, useContext, useEffect, useRef } from 'react'
import styled, { useTheme } from 'styled-components'
import SelectedMapItemPin from './Pins/SelectedMapItemPin'
import type { BaseMapElementComponentProps } from '../utils'
import { pinOffSetter } from '../utils'
import Clickable from 'components/Common/Clickable'
import { leOfferSelectionId } from 'tripPlanner/utils/itemSelection'
import { TripMapContext } from 'tripPlanner/contexts/TripMapContext'
import TeardropPin from './Pins/TeardropPin'
import { getThemeClassName } from 'lib/theme/themeUtils'
import LineTicketIcon from 'LuxKit/Icons/line/LineTicketIcon'
import LineBedIcon from 'LuxKit/Icons/line/LineBedIcon'
import { isExperienceOffer } from 'lib/offer/offerTypes'
import useToggle from 'hooks/useToggle'

const StyledClickable = styled(Clickable)`
  position: absolute;
  left: 0;
  bottom: 0;
  transform: translateX(-50%);
`

interface Props extends BaseMapElementComponentProps {
  offer: App.HotelOffer | App.ExperienceOffer
}

function LEOfferMapMarker({
  active,
  offer,
  position,
  zIndex,
}: Props) {
  const { onSelectMapItem, onDeselectMapItem } = useContext(TripMapContext)
  const theme = useTheme()
  const Icon = isExperienceOffer(offer) ? LineTicketIcon : LineBedIcon
  const name = isExperienceOffer(offer) ? offer.name : offer.property.name
  const pinColor = theme.palette.neutral.default.two
  const selectedPinColor = theme.palette.neutral.default.eight
  const onClick: MouseEventHandler = useCallback(
    (e) => {
      e.preventDefault()
      e.stopPropagation()
      if (active) {
        onDeselectMapItem()
      } else {
        onSelectMapItem(leOfferSelectionId(offer))
      }
    },
    [active, onDeselectMapItem, onSelectMapItem, offer],
  )

  const ref = useRef<HTMLButtonElement | null>(null)

  useEffect(() => {
    if (ref.current && google?.maps) {
      google.maps.OverlayView.preventMapHitsFrom(ref.current)
    }
  }, [ref])

  const [hovered, , onHover, offHover] = useToggle(false)

  const overlayZIndex = active || hovered ? 1000 : zIndex

  return (
    <OverlayViewF
      zIndex={overlayZIndex}
      position={position}
      getPixelPositionOffset={pinOffSetter}
      mapPaneName="overlayMouseTarget"
    >
      <StyledClickable
        onClick={onClick}
        ref={ref}
        onMouseEnter={onHover}
        onMouseLeave={offHover}
      >
        {!active && (
          <TeardropPin
            isActive={false}
            className={getThemeClassName({ mobile: 'inverse' })}
            pinColour={pinColor}
            icon={Icon}
            text={name}
          />
        )}
        {active && (
          <SelectedMapItemPin
            pinColour={selectedPinColor}
            icon={Icon}
            strokeColour={theme.palette.neutral.default.one}
            iconColour="neutral-two"
          />
        )}
      </StyledClickable>
    </OverlayViewF>
  )
}

export default memo(LEOfferMapMarker)
